import { useCallback, useMemo } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import {
  IPrioritySchedulerColumnData,
  MiIcon,
  MiIconButton,
  PrioritySchedulerBoard,
} from "@miplatform/components";
import { TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  IUserListItemResponse,
} from "@miplatform/types";
import { getDateDay, getWorkingDays, isSame } from "@miplatform/utils";
import { mdiArrowCollapse, mdiArrowExpand, mdiClose } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import {
  NHC_ASSIGNMENT_BOARD_DAY_COUNT,
  type StagedWorkOrderAssignment,
  toBoardEventFromStagedAssignment,
} from "../NHCPriorityScheduler/useAssignmentStaging";
import { buildDisabledSlots } from "../buildColumns.util";

const VISIBLE_COLUMN_COUNT = 5;
const AUTO_ASSIGN_WINDOWS_WIDTH = 22;
const BASE_GUTTER_EXTRA = 55;

interface AssignableUserColumnProps {
  user: IUserListItemResponse;
  selectedDate: Moment;
  selected?: boolean;
  columnWidth: number;
  hourHeight: number;
  zoom?: number;
  onClose?: () => void;
  boardExpanded: boolean;
  onBoardExpandedChange: (expanded: boolean) => void;
  stagedAssignments?: StagedWorkOrderAssignment[];
  assignedEvents?: IAttemptListItemResponseForWorkOrder[];
  onRemoveStagedAttempt?: (entityId: string) => void;
}

const AssignableUserColumn = ({
  user,
  selectedDate,
  selected = false,
  columnWidth,
  hourHeight,
  onClose,
  boardExpanded,
  onBoardExpandedChange,
  stagedAssignments = [],
  assignedEvents = [],
  onRemoveStagedAttempt,
  zoom,
}: Readonly<AssignableUserColumnProps>) => {
  const { calendarSet, tenantHolidays } = useCalendarContext();

  const hasAutoAssignWindows = (
    calendarSet?.config?.workTemplateBases ?? []
  ).some((t) => t.autoAssignWindows.length > 0);

  const gutterExtra =
    BASE_GUTTER_EXTRA + (hasAutoAssignWindows ? AUTO_ASSIGN_WINDOWS_WIDTH : 0);

  const { classes, cx } = useStyles({
    columnWidth,
    boardExpanded,
    gutterExtra,
  });

  const toggleExpanded = useCallback(() => {
    onBoardExpandedChange(!boardExpanded);
  }, [boardExpanded, onBoardExpandedChange]);

  const allColumns = useMemo<IPrioritySchedulerColumnData[]>(() => {
    const start = selectedDate.clone().startOf("day");
    return getWorkingDays(start, NHC_ASSIGNMENT_BOARD_DAY_COUNT).map(
      (date, i) => {
        const dayAssignedEvents = assignedEvents.filter((e) => {
          const sd = e.scheduledStartDate ?? e.startDate ?? e.scheduleDate;
          if (!sd) {
            return false;
          }
          return isSame({ date1: sd, date2: date.toDate(), timeUnit: "day" });
        });
        const dayStagedEvents = stagedAssignments
          .filter((s) => s.dayOffset === i)
          .map(toBoardEventFromStagedAssignment);

        return {
          id: `${user.id}-column-${date.toISOString()}`,
          user,
          date,
          dateView: true,
          events: [...dayAssignedEvents, ...dayStagedEvents],
          disabledSlots: buildDisabledSlots({
            user,
            date,
          }),
          columnHeaderHeight: 0,
        };
      }
    );
  }, [user, selectedDate.valueOf(), stagedAssignments, assignedEvents]);

  const visibleColumns = useMemo(() => {
    if (boardExpanded) {
      return allColumns;
    }
    const [first] = allColumns;
    if (!first) {
      return allColumns;
    }

    return [
      {
        ...first,
        events: [
          ...assignedEvents,
          ...stagedAssignments.map(toBoardEventFromStagedAssignment),
        ],
      },
    ];
  }, [allColumns, boardExpanded, stagedAssignments, assignedEvents]);

  const displayName =
    user.displayName || user.name || `${user.firstName} ${user.lastName}`;

  return (
    <Box
      className={cx(
        classes.column,
        selected && classes.columnSelected,
        boardExpanded && classes.columnExpanded
      )}
    >
      <Box className={cx(classes.header, selected && classes.headerSelected)}>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
          noWrap
          className={classes.userName}
        >
          {displayName}
        </Typography>
        <Box className={classes.actions}>
          <MiIconButton onClick={toggleExpanded} data-testid="expand-button">
            <MiIcon
              path={boardExpanded ? mdiArrowCollapse : mdiArrowExpand}
              size={0.75}
            />
          </MiIconButton>
          <MiIconButton onClick={onClose} data-testid="close-button">
            <MiIcon path={mdiClose} size={0.75} />
          </MiIconButton>
        </Box>
      </Box>
      <Box className={classes.body}>
        <PrioritySchedulerBoard
          columns={visibleColumns}
          columnWidth={columnWidth}
          tenantHolidays={tenantHolidays}
          hourHeight={hourHeight}
          date={getDateDay(selectedDate)}
          onEventRemove={onRemoveStagedAttempt}
          calendarSet={calendarSet?.config ?? null}
          zoom={zoom}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  columnWidth: number;
  boardExpanded: boolean;
  gutterExtra: number;
}>()((theme, { columnWidth, boardExpanded, gutterExtra }) => ({
  column: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
    gap: theme.spacing(1),
    width: columnWidth + gutterExtra,
  },
  columnSelected: {
    borderColor: theme.palette.primary.main,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey.light,
    height: 34,
  },
  headerSelected: {
    backgroundColor: theme.palette.primary.light,
  },
  userName: {
    flex: 1,
    fontWeight: theme.fontWeight.semiBold,
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  body: {
    flex: 1,
    padding: theme.spacing(1),
    paddingTop: 0,
    overflowX: boardExpanded ? "auto" : "hidden",
    overflowY: "auto",
  },
  columnExpanded: {
    width: VISIBLE_COLUMN_COUNT * columnWidth + gutterExtra,
    padding: theme.spacing(1, 0),
    paddingTop: 0,
  },
}));

export default AssignableUserColumn;
